import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useUpdateMerchantSettings } from '@/api/domains/merchant-settings';
import { toApiProblem, toastProblem } from '@/lib/errors';

import { SettingsCard, SettingsSection, SettingsToggleRow } from '../components/SettingsSection';

interface FulfillmentSectionProps {
  syncEnabled: boolean;
  notifyCustomer: boolean;
}

/**
 * Shopify fulfillment sync: creating an InPost shipment marks the order
 * fulfilled with the tracking number attached (that's the only way tracking
 * reaches a Shopify order), optionally emailing the buyer Shopify's shipping
 * confirmation. With sync off the app never fulfills, but still attaches
 * tracking to fulfillments the merchant creates by hand (no email). Both
 * toggles save immediately.
 */
export function FulfillmentSection({ syncEnabled, notifyCustomer }: FulfillmentSectionProps) {
  const { t } = useTranslation('settings');

  const update = useUpdateMerchantSettings();

  const [pendingSync, setPendingSync] = useState<boolean | null>(null);
  const [pendingNotify, setPendingNotify] = useState<boolean | null>(null);
  const syncChecked = pendingSync ?? syncEnabled;
  const notifyChecked = pendingNotify ?? notifyCustomer;

  useEffect(() => {
    if (pendingSync !== null && pendingSync === syncEnabled) {
      setPendingSync(null);
    }
  }, [pendingSync, syncEnabled]);

  useEffect(() => {
    if (pendingNotify !== null && pendingNotify === notifyCustomer) {
      setPendingNotify(null);
    }
  }, [pendingNotify, notifyCustomer]);

  const save = (
    key: 'fulfillment_sync_enabled' | 'fulfillment_notify_customer',
    next: boolean,
    setPending: (value: boolean | null) => void,
  ) => {
    if (update.isPending) return;
    setPending(next);
    update.mutate(
      { settings: { [key]: next } },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: (error) => {
          setPending(null);
          toastProblem(toApiProblem(error));
        },
      },
    );
  };

  return (
    <SettingsSection
      title={t('general.fulfillment.title')}
      description={t('general.fulfillment.description')}
    >
      <SettingsCard>
        <SettingsToggleRow
          label={t('general.fulfillment.syncLabel')}
          description={t('general.fulfillment.syncDescription')}
          checked={syncChecked}
          onCheckedChange={(next) => save('fulfillment_sync_enabled', next, setPendingSync)}
        />
        {syncChecked ? (
          <SettingsToggleRow
            label={t('general.fulfillment.notifyLabel')}
            description={t('general.fulfillment.notifyDescription')}
            checked={notifyChecked}
            onCheckedChange={(next) => save('fulfillment_notify_customer', next, setPendingNotify)}
          />
        ) : null}
      </SettingsCard>
    </SettingsSection>
  );
}
